import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { isProductionDomain } from '@/lib/productionDomainCheck';
import { getDeviceType, getBrowser, isLikelyBot } from '@/lib/botDetection';

// Constants for presence tracking
const VISITOR_ID_KEY = 'xpand_visitor_id';
const SESSION_ID_KEY = 'xpand_session_id';
const HEARTBEAT_INTERVAL = 5000; // 5 seconds heartbeat
const JOURNEY_KEY = 'xpand_journey_step';

function getVisitorId(): string {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return visitorId;
}

function getSessionId(): string {
  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  if (stored) {
    try {
      const { id, lastActivity } = JSON.parse(stored);
      if (Date.now() - lastActivity < 30 * 60 * 1000) {
        return id;
      }
    } catch {
      // Parse error, create new session
    }
  }
  
  const newId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem(SESSION_ID_KEY, JSON.stringify({
    id: newId,
    lastActivity: Date.now(),
  }));
  return newId;
}

function getJourneyStep(): number {
  const stored = sessionStorage.getItem(JOURNEY_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

function setJourneyStep(step: number): void {
  sessionStorage.setItem(JOURNEY_KEY, step.toString());
}

/**
 * Hook that tracks user presence on pages in real-time
 * and records complete user journeys for analytics
 */
// Only admin paths should be excluded from live presence tracking
// Seller pages, dashboard, and all other public pages should be tracked
function isExcludedPath(path: string): boolean {
  return path.startsWith('/admin');
}

// Check if tracking should happen - synchronous check each time (plain function, not hook)
function shouldTrack(pagePath: string): boolean {
  const isProd = isProductionDomain();
  const isBot = isLikelyBot();
  const isExcluded = isExcludedPath(pagePath);
  
  return isProd && !isBot && !isExcluded;
}

export function usePresenceTracking() {
  const location = useLocation();
  const heartbeatInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pageEntryTime = useRef<number>(Date.now());
  const currentPath = useRef<string>(location.pathname);
  const lastJourneyId = useRef<string | null>(null);
  const scrollDepth = useRef<number>(0);
  const interactionCount = useRef<number>(0);

  // Update presence in database
  const updatePresence = useCallback(async (pagePath: string) => {
    // Synchronous check - no race condition
    if (!shouldTrack(pagePath)) return;

    const sessionId = getSessionId();
    const visitorId = getVisitorId();

    try {
      // Get user if authenticated
      let userId: string | null = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        userId = user?.id || null;
      } catch {
        // Ignore auth errors
      }

      // Upsert presence record
      const { error } = await supabase
        .from('live_presence')
        .upsert({
          session_id: sessionId,
          visitor_id: visitorId,
          user_id: userId,
          current_page: pagePath,
          device_type: getDeviceType(),
          browser: getBrowser(),
          last_heartbeat: new Date().toISOString(),
        }, {
          onConflict: 'session_id',
        });

      if (error) {
        console.error('[Presence] Update error:', error);
      }
    } catch (error) {
      console.error('[Presence] Error:', error);
    }
  }, []);

  // Record journey step when navigating to a new page
  const recordJourneyStep = useCallback(async (pagePath: string, referrerPage: string | null) => {
    // Synchronous check - no race condition
    if (!shouldTrack(pagePath)) return;

    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    const stepNumber = getJourneyStep() + 1;
    setJourneyStep(stepNumber);

    try {
      // Get user if authenticated
      let userId: string | null = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        userId = user?.id || null;
      } catch {
        // Ignore auth errors
      }

      // Insert journey step
      const { data, error } = await supabase
        .from('user_journeys')
        .insert({
          session_id: sessionId,
          visitor_id: visitorId,
          user_id: userId,
          page_path: pagePath,
          page_title: document.title,
          step_number: stepNumber,
          referrer_page: referrerPage,
          entered_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        console.error('[Journey] Insert error:', error);
      } else if (data) {
        lastJourneyId.current = data.id;
      }
    } catch (error) {
      console.error('[Journey] Error:', error);
    }
  }, []);

  // Update journey step when leaving a page (with time on page, scroll depth, etc.)
  const updateJourneyExit = useCallback(async () => {
    // Check production status synchronously
    if (!isProductionDomain() || !lastJourneyId.current) return;

    const timeOnPage = Date.now() - pageEntryTime.current;

    try {
      await supabase
        .from('user_journeys')
        .update({
          exited_at: new Date().toISOString(),
          time_on_page_ms: timeOnPage,
          scroll_depth: scrollDepth.current,
          interaction_count: interactionCount.current,
        })
        .eq('id', lastJourneyId.current);
    } catch (error) {
      console.error('[Journey] Exit update error:', error);
    }
  }, []);

  // Remove presence when leaving
  const removePresence = useCallback(async () => {
    // Check production status synchronously
    if (!isProductionDomain()) return;

    const sessionId = getSessionId();

    try {
      await supabase
        .from('live_presence')
        .delete()
        .eq('session_id', sessionId);
    } catch (error) {
      console.error('[Presence] Remove error:', error);
    }
  }, []);

  // Track page changes
  useEffect(() => {
    const previousPath = currentPath.current;
    currentPath.current = location.pathname;

    // Reset tracking state for new page
    scrollDepth.current = 0;
    interactionCount.current = 0;

    // Update previous page's journey record with exit data
    if (previousPath !== location.pathname && lastJourneyId.current) {
      updateJourneyExit();
    }

    // Update presence and record new journey step
    pageEntryTime.current = Date.now();
    updatePresence(location.pathname);
    recordJourneyStep(
      location.pathname,
      previousPath !== location.pathname ? previousPath : null
    );
  }, [location.pathname, updatePresence, recordJourneyStep, updateJourneyExit]);

  // Heartbeat to keep presence alive
  useEffect(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(() => {
      updatePresence(location.pathname);
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [location.pathname, updatePresence]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      scrollDepth.current = Math.max(scrollDepth.current, scrollPercent);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track interactions (clicks)
  useEffect(() => {
    const handleClick = () => {
      interactionCount.current++;
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Cleanup on unmount and page exit - use fetch with keepalive for proper PATCH requests
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Synchronous check - no ref dependency
      if (!isProductionDomain()) return;

      // Update journey exit with keepalive fetch (supports PATCH unlike sendBeacon)
      if (lastJourneyId.current) {
        const timeOnPage = Date.now() - pageEntryTime.current;
        
        fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_journeys?id=eq.${lastJourneyId.current}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
              'Prefer': 'return=minimal',
            },
            body: JSON.stringify({
              exited_at: new Date().toISOString(),
              time_on_page_ms: timeOnPage,
              scroll_depth: scrollDepth.current,
              interaction_count: interactionCount.current,
            }),
            keepalive: true,
          }
        ).catch(() => {
          // Silently fail - page is closing anyway
        });
      }

      // Remove presence
      const sessionId = getSessionId();
      fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/live_presence?session_id=eq.${sessionId}`,
        {
          method: 'DELETE',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          keepalive: true,
        }
      ).catch(() => {
        // Silently fail
      });
    };

    // Also handle visibility change for mobile devices that don't fire beforeunload
    const handleVisibilityChange = () => {
      // Synchronous check
      if (document.visibilityState === 'hidden' && isProductionDomain()) {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removePresence();
      updateJourneyExit();
    };
  }, [removePresence, updateJourneyExit]);
}
